import { IPosition } from '../../types/common.types';
import { MapStyle } from '../../types/map.types';
import { STYLE_PRESETS } from './style-presets';
import { PlacedRoom } from './placement';

type Grid = number[][];

function inBounds(grid: Grid, x: number, y: number): boolean {
  return y >= 0 && y < grid.length && x >= 0 && x < grid[0].length;
}

function carveDisc(grid: Grid, cx: number, cy: number, half: number): void {
  for (let dy = -half; dy <= half; dy++) {
    for (let dx = -half; dx <= half; dx++) {
      if (inBounds(grid, cx + dx, cy + dy)) {
        grid[cy + dy][cx + dx] = 1;
      }
    }
  }
}

function doorCell(room: PlacedRoom, target: IPosition): IPosition {
  let best = room.cells[0];
  let bestD = Infinity;
  for (const c of room.cells) {
    const dx = c.x - target.x;
    const dy = c.y - target.y;
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

function carveStraight(
  grid: Grid,
  a: IPosition,
  b: IPosition,
  half: number,
): void {
  if (Math.random() < 0.5) {
    for (let x = Math.min(a.x, b.x); x <= Math.max(a.x, b.x); x++) {
      carveDisc(grid, x, a.y, half);
    }
    for (let y = Math.min(a.y, b.y); y <= Math.max(a.y, b.y); y++) {
      carveDisc(grid, b.x, y, half);
    }
  } else {
    for (let y = Math.min(a.y, b.y); y <= Math.max(a.y, b.y); y++) {
      carveDisc(grid, a.x, y, half);
    }
    for (let x = Math.min(a.x, b.x); x <= Math.max(a.x, b.x); x++) {
      carveDisc(grid, x, b.y, half);
    }
  }
}

function carveDrunk(
  grid: Grid,
  a: IPosition,
  b: IPosition,
  half: number,
): void {
  let cx = a.x;
  let cy = a.y;
  let guard = 0;
  const maxSteps = (grid.length + grid[0].length) * 4;
  while ((cx !== b.x || cy !== b.y) && guard < maxSteps) {
    guard++;
    carveDisc(grid, cx, cy, half);
    if (Math.random() < 0.6) {
      const ddx = b.x - cx;
      const ddy = b.y - cy;
      if (Math.abs(ddx) >= Math.abs(ddy)) {
        cx += ddx > 0 ? 1 : -1;
      } else {
        cy += ddy > 0 ? 1 : -1;
      }
    } else {
      const dir = Math.floor(Math.random() * 4);
      if (dir === 0 && cx + 1 < grid[0].length - 1) {
        cx++;
      } else if (dir === 1 && cx - 1 > 0) {
        cx--;
      } else if (dir === 2 && cy + 1 < grid.length - 1) {
        cy++;
      } else if (dir === 3 && cy - 1 > 0) {
        cy--;
      }
    }
  }
  carveDisc(grid, b.x, b.y, half);
}

export function carveCorridors(
  grid: Grid,
  rooms: PlacedRoom[],
  pairs: [number, number][],
  style: MapStyle,
): void {
  const p = STYLE_PRESETS[style];
  const half = Math.floor(p.corridorWidth / 2);
  for (const [ai, bi] of pairs) {
    const a = rooms[ai];
    const b = rooms[bi];
    const da = doorCell(a, b.center);
    const db = doorCell(b, a.center);
    if (Math.random() < p.corridorWindiness) {
      carveDrunk(grid, da, db, half);
    } else {
      carveStraight(grid, da, db, half);
    }
  }
}
